// The Fleet Designer of the WARDROOM panel (monetization.md §3.2, R1249):
// two pages of the section shell, FLEET NAMES and FLEET LIVERY (R2186).
// One card object draws both bodies (`namesHtml`, `liveryHtml`) and keeps
// both drafts, so a draft lives on while the reader walks from one page to
// the other. Both pages wear the tier tag of the `fleet_designer` key.
//
// FLEET NAMES edits one draft of the fleet name pool (`fleetnames.ts`) and
// sends it whole with one press of SAVE. One row is one name, with a ✕ that
// removes it, and ADD A NAME puts an empty row at the end. CLEAR THE POOL
// takes two presses (R837), because it throws the whole list away.
//
// Without the key the card names the tier and points at the shop. A lapsed
// key keeps the stored pool, and the world goes on drawing from it
// (R1241), so the card then shows the stored names and CLEAR THE POOL,
// which needs no key. Every control is a button of the choice kit that
// acts on a press (R2011), so nothing depends on hover (ui-map-interface.md §12).
//
// FLEET LIVERY (R1252) holds NONE and the twenty options as previews of
// one hull in the viewer's faction ink (`liverypreview.ts`); one press
// picks (`liverypick.ts`), and SAVE sends the pick. A lapsed key keeps the
// stored livery and shows REMOVE THE LIVERY, which needs no key. Each group
// lays out in an even split of rows (`liveryCols`): 5 x 2 on a desktop
// page and 2 x 5 on a telephone, and a tile name wraps and never cuts
// (`.fd-lv` in hub.css, R2186).

import { sellsNothing } from '../../../platform/platform';
import { runeCount, tidySpaces } from '../../../util/text';
import { button, previewTiles, tag } from '../../choice/choice';
import {
  ApiError, KEY_FLEET_DESIGNER, getFleetLivery, getFleetNames, messageOf, putFleetLivery,
  putFleetNames, resetFleetLivery, resetFleetNames,
} from '../../../net/api';
import { TTL_ON_OPEN, hasKey, hubFaction, type Cell } from '../hubstate';
import { factionColor } from '../../../world';
import type { LiveryCode } from '../../../gen/presentation';
import { liveryNameKey } from '../../../render/livery';
import {
  LIVERY_GROUPS, asPick, liveryDraftOf, liverySave, pickLivery, type LiveryDraft, type LiveryPick,
} from './liverypick';
import { PREVIEW_H, PREVIEW_W, liveryPreviewUrl } from './liverypreview';
import { CHROME_ARM_MS, HUB_SECTIONS } from '../../../gen/presentation';
import {
  CODE_INVALID_FLEET_NAMES, FLEET_NAME_MAX_RUNES, FLEET_NAME_MIN_RUNES, FLEET_POOL_MAX_NAMES,
} from '../../../gen/balance';
import { ArmGate } from '../../arm';
import { hubWardroom as S } from '../../../gen/strings';
import { wordOf } from '../../../locale/wordof';
import {
  armedButton, armedPress, empty, esc, lede, pending, ctlRow, plain, tierTag, wireOnce,
  type PanelContext,
} from '../panel';
import {
  addRow, canAdd, changed, draftOf, namesOf, precheck, removeRow, type PoolDraft,
} from './fleetnames';

/** The cache key of the pool read. */
const POOL_KEY = 'fleet-names';

/** The cache key of the livery read (R1252). */
const LIVERY_KEY = 'fleet-livery';

/** The account's livery code, '' for NONE, through the hub cache. */
export function fleetLiveryCell(ctx: Pick<PanelContext, 'data'>): Cell<string> {
  return ctx.data.cell<string>(LIVERY_KEY, TTL_ON_OPEN, getFleetLivery);
}

/** The name of one pick, in the reader's language. */
export function liveryName(pick: LiveryPick): string {
  if (pick === '') return S.livery_none;
  return wordOf(S, liveryNameKey(pick), pick);
}

/** The account's pool, through the hub cache. */
export function fleetNamesCell(ctx: Pick<PanelContext, 'data'>): Cell<string[]> {
  return ctx.data.cell<string[]>(POOL_KEY, TTL_ON_OPEN, getFleetNames);
}

// The three form primitives the two designers share (`.cd` in hub.css):
// the Captain Designer of `wardroom.ts` draws its form with them too.

/** A rune counter. Past `max` it wears the refusal colour. */
export function counter(key: string, n: number, max: number): string {
  return `<span class="cdc${n > max ? ' bad' : ''}" data-count="${key}">${esc(S.count({ n, max }))}</span>`;
}

/** The label row of one field: the name on the left, the counter on the right. */
export function fieldHead(label: string, right = ''): string {
  return `<div class="cdh"><span>${esc(label)}</span>${right}</div>`;
}

/** The refusal line of one field of a form. */
export function formErr(text: string): string {
  return `<div class="cde">${esc(text)}</div>`;
}

/** The outcome line of a form, or '' while there is none. */
export function formNote(text: string): string {
  return text === '' ? '' : `<div class="cdn">${esc(text)}</div>`;
}

/** SEE THE SHOP of a WARDROOM page whose key the account does not hold,
 *  or nothing in a store app, which sells nothing (R2927). */
export function shopPress(): string {
  if (sellsNothing()) return '';
  return button(S.see_the_shop, { act: 'wardroom-shop' }, { variant: 'primary' });
}

/** Moves one counter on a keystroke, without a rewrite of the pane. */
export function showCount(root: HTMLElement, key: string, n: number, max: number): void {
  const el = root.querySelector<HTMLElement>(`[data-count="${key}"]`);
  if (!el) return;
  el.textContent = S.count({ n, max });
  el.classList.toggle('bad', n > max);
}

/**
 * The columns of one livery group of `n` tiles, at most `max` (R2186): the
 * largest divisor of `n` from 2 to `max`, so every row is full; where no
 * such divisor exists, `max` itself (or `n` when the group is smaller).
 */
export function liveryCols(n: number, max: number): number {
  for (let c = Math.min(n, max); c >= 2; c--) if (n % c === 0) return c;
  return Math.max(1, Math.min(n, max));
}

/** The Fleet Designer card. The WARDROOM panel owns it, draws its two
 *  pages from it and hands it every action that starts with `fd-`. */
export class FleetDesignerCard {
  /** The draft, made from the first read the card saw. */
  private draft?: PoolDraft;
  /** The call in flight, if any. */
  private busy: '' | 'save' | 'reset' = '';
  /** The last refusal: the row it names, -1 for the card as a whole. */
  private err?: { index: number; text: string };
  /** The plain outcome of the last call. */
  private notice = '';
  /** The two-press gate of CLEAR THE POOL and REMOVE THE LIVERY
   *  (`ui/arm.ts`, R837). */
  private gate = new ArmGate(CHROME_ARM_MS, () => this.ctx.refresh());
  /** The livery draft (R1252), made from the first read the card saw. */
  private lv?: LiveryDraft;
  /** The livery call in flight, the last refusal and the outcome. */
  private lvBusy = false;
  private lvErr = '';
  private lvNotice = '';

  constructor(private ctx: PanelContext) {}

  /** Drops an armed press, when the panel closes. */
  clear(): void {
    this.gate.clear(false);
  }

  /**
   * What the hulls wear in the draft, before a SAVE (R1912): the livery
   * picked and the names the pool sends (none for an empty pool). A part
   * is undefined while the card has no draft of it: the read has not
   * answered, or the account does not hold the key. The YOUR HULLS column
   * of FLEET LIVERY and the naming diagram of FLEET NAMES read it, so a
   * column follows each pick and each keystroke; a pick redraws the pane,
   * and a keystroke shows at the next tick of the hub, which redraws a
   * pane whose markup changed.
   */
  draftLook(): { livery?: LiveryPick; names?: string[] } {
    return {
      livery: this.lv?.picked,
      names: this.draft === undefined ? undefined : namesOf(this.draft),
    };
  }

  /** True while the account holds the key. */
  private held(): boolean {
    return hasKey(this.ctx.me(), KEY_FLEET_DESIGNER);
  }

  /** The tier tag of both pages, which stands in the page title (R1945):
   *  the key is in the Supporter plan and in Premium, so a Premium account
   *  reads INCLUDED IN PREMIUM (R2046, R2186). */
  mark(): string {
    return tierTag('supporter', this.held(), this.ctx.me());
  }

  /** The body of the FLEET NAMES page: no box and no title, because the
   *  page title names it (R1945). */
  namesHtml(): string {
    const head = lede(S.fleet_lede);
    const cell = fleetNamesCell(this.ctx);
    if (!this.held()) return plain(head + this.locked(cell.value));
    if (cell.value === undefined) return plain(head + pending(cell));
    if (this.draft === undefined) this.draft = draftOf(cell.value);
    return plain(head + this.form(this.draft, cell.value));
  }

  /** The body of the FLEET LIVERY page: NONE and the two groups, then
   *  SAVE; without the key, a stored livery with its removal, else the
   *  shop. */
  liveryHtml(): string {
    return plain(this.held() ? this.livery() : this.liveryLocked());
  }

  // -- the livery (R1252) ----------------------------------------------------

  /** The livery form with the key: NONE and the options, then SAVE. */
  private livery(): string {
    const cell = fleetLiveryCell(this.ctx);
    if (cell.value === undefined) return '<div class="cd">' + pending(cell) + '</div>';
    if (this.lv === undefined) this.lv = liveryDraftOf(cell.value);
    const d = this.lv;
    const ink = factionColor(hubFaction(this.ctx.me()) || 1);
    const groups = LIVERY_GROUPS.map((g) =>
      fieldHead(g.family === 'pattern' ? S.livery_patterns : S.livery_decals)
      + this.liveryTiles(g.codes, d.picked, ink, g.codes.length),
    ).join('');
    const save = button(this.lvBusy ? S.saving : S.save, { act: 'fd-lv-save' },
      { variant: 'primary', disabled: this.lvBusy });
    // NONE takes one cell of the grid of the first group, so it stands as
    // wide as every other tile.
    return '<div class="cd fd-lv">'
      + fieldHead(S.livery_label,
        `<span class="cdc">${esc(S.livery_picked({ name: liveryName(d.picked) }))}</span>`)
      + lede(S.livery_lede)
      + this.liveryTiles([''], d.picked, ink, LIVERY_GROUPS[0]?.codes.length ?? 1)
      + groups
      + lede(S.livery_hint)
      + ctlRow(save)
      + this.lvLines()
      + '</div>';
  }

  /** The liveries of one group as preview tiles of the choice kit: the
   *  preview over the name, one press picks (R2011). `count` sets the
   *  columns of the group block (`liveryCols`): the wide count and the
   *  narrow count ride two custom properties, and a container query of
   *  hub.css picks one by the width of the block (R2186). */
  private liveryTiles(
    codes: readonly (LiveryCode | '')[], picked: LiveryPick, ink: number, count: number,
  ): string {
    const tiles = previewTiles(codes.map((code) => {
      const url = liveryPreviewUrl(code, ink, HUB_SECTIONS.liveryTileScale);
      return {
        id: code, label: liveryName(code),
        picture: url === ''
          ? '<span class="lvimg" aria-hidden="true"></span>'
          : `<img class="lvimg" src="${url}" width="${PREVIEW_W}" height="${PREVIEW_H}" alt="">`,
      };
    }), picked, 'fd-lv-pick', { label: S.livery_label });
    const wide = liveryCols(count, HUB_SECTIONS.liveryColsWide);
    const narrow = liveryCols(count, HUB_SECTIONS.liveryColsNarrow);
    return `<div class="fd-lvg" style="--lv-w:${wide};--lv-n:${narrow}">${tiles}</div>`;
  }

  /** The page without the key: the shop, and a stored livery with its
   *  removal, because a lapsed key keeps it (R1241). */
  private liveryLocked(): string {
    const shop = shopPress();
    const stored = asPick(fleetLiveryCell(this.ctx).value ?? '');
    if (stored === '') return lede(S.livery_lede) + ctlRow(shop);
    const remove = armedButton(this.gate, 'fd-lv-reset', S.livery_reset, S.livery_reset_armed,
      this.lvBusy ? null : { act: 'fd-lv-reset' });
    return lede(S.livery_stored({ name: liveryName(stored) })) + ctlRow(shop + remove) + this.lvLines();
  }

  /** The refusal and the outcome lines of the section. */
  private lvLines(): string {
    return (this.lvErr === '' ? '' : formErr(this.lvErr)) + formNote(this.lvNotice);
  }

  /** One action of the section; false when the action is not the livery's. */
  private liveryAct(action: string, el: HTMLElement): boolean {
    switch (action) {
      case 'fd-lv-pick':
        if (this.lv === undefined || this.lvBusy || !pickLivery(this.lv, el.dataset.id ?? '')) return true;
        this.ctx.play('sfx-menu-toggle');
        this.lvErr = '';
        this.lvNotice = '';
        this.ctx.refresh();
        return true;
      case 'fd-lv-save': {
        const d = this.lv;
        if (d === undefined || this.lvBusy) return true;
        const what = liverySave(d);
        if (what === 'none') {
          this.lvErr = '';
          this.lvNotice = S.saved;
          this.ctx.refresh();
          return true;
        }
        const code = d.picked;
        this.runLivery(what === 'put' ? () => putFleetLivery(code) : resetFleetLivery,
          what === 'put' ? S.saved : S.livery_reset_done);
        return true;
      }
      case 'fd-lv-reset':
        if (!armedPress(this.ctx, this.gate, 'fd-lv-reset')) return true;
        this.runLivery(resetFleetLivery, S.livery_reset_done);
        return true;
      default:
        return false;
    }
  }

  /** One livery call. The answer is the stored code, which becomes the
   *  draft; the cell refreshes behind it. */
  private runLivery(call: () => Promise<string>, done: string): void {
    this.lvBusy = true;
    this.lvErr = '';
    this.lvNotice = '';
    this.ctx.play('sfx-menu-press');
    this.ctx.refresh();
    call().then((code) => {
      this.lvBusy = false;
      this.lv = liveryDraftOf(code);
      this.lvNotice = done;
      this.ctx.data.expire(LIVERY_KEY);
      this.ctx.refresh();
    }, (err: unknown) => {
      this.lvBusy = false;
      this.ctx.play('sfx-menu-error');
      this.lvErr = messageOf(err);
      this.ctx.refresh();
    });
  }

  /** The card without the key: the shop, and a stored pool with its
   *  clear, because a lapsed key keeps drawing from it (R1241). */
  private locked(stored: string[] | undefined): string {
    const shop = shopPress();
    if (stored === undefined || stored.length === 0) return ctlRow(shop);
    return lede(S.fleet_stored({ names: stored.join(', ') }))
      + ctlRow(shop + this.resetButton())
      + this.errLine(-1)
      + formNote(this.notice);
  }

  /** The refusal line of one row, or ''. */
  private errLine(index: number): string {
    return this.err !== undefined && this.err.index === index ? formErr(this.err.text) : '';
  }

  /** CLEAR THE POOL, behind the two-press gate. */
  private resetButton(): string {
    return armedButton(this.gate, 'fd-reset', S.fleet_reset, S.fleet_reset_armed,
      this.busy === '' ? { act: 'fd-reset' } : null);
  }

  /** The form: one row a name, then the controls. Every block stands in
   *  one column, so the card reads the same on a telephone. */
  private form(d: PoolDraft, stored: string[]): string {
    const busy = this.busy !== '';
    const rows = d.rows.map((row, i) => '<div class="cdv">'
      + `<input data-in="fd-name-${i}" type="text" autocomplete="off" `
      + `maxlength="${FLEET_NAME_MAX_RUNES * 2}" `
      + `aria-label="${esc(S.fleet_placeholder({ n: i + 1 }))}" `
      + `placeholder="${esc(S.fleet_placeholder({ n: i + 1 }))}" value="${esc(row)}">`
      + counter(`fd-name-${i}`, runeCount(tidySpaces(row)), FLEET_NAME_MAX_RUNES)
      + button(S.fleet_remove, { act: 'fd-remove', data: { i: String(i) } },
        { variant: 'danger', title: S.fleet_remove_label({ n: i + 1 }) })
      + '</div>' + this.errLine(i)).join('');
    const save = button(this.busy === 'save' ? S.saving : S.save, { act: 'fd-save' },
      { variant: 'primary', disabled: busy });
    const add = canAdd(d)
      ? button(S.fleet_add, { act: 'fd-add' }, { disabled: busy })
      : tag(S.fleet_full({ max: FLEET_POOL_MAX_NAMES }));
    const none = stored.length === 0 && namesOf(d).length === 0
      ? empty(S.fleet_empty, S.fleet_empty_why) : '';
    return '<div class="cd">'
      + fieldHead(S.fleet_label, '<span class="cdc" data-count="fd-all">'
        + `${esc(S.fleet_count({ n: namesOf(d).length, max: FLEET_POOL_MAX_NAMES }))}</span>`)
      + none
      + rows
      + ctlRow(add)
      + lede(S.fleet_hint({ min: FLEET_NAME_MIN_RUNES, max: FLEET_NAME_MAX_RUNES }))
      + ctlRow(save + (stored.length > 0 ? this.resetButton() : ''))
      + this.errLine(-1)
      + formNote(this.notice)
      + '</div>';
  }

  /** One action of the card. */
  act(action: string, el: HTMLElement): void {
    if (this.liveryAct(action, el)) return;
    const d = this.draft;
    switch (action) {
      case 'fd-add':
        if (d === undefined || !addRow(d)) return;
        this.ctx.play('sfx-menu-press');
        this.notice = '';
        this.ctx.refresh();
        return;
      case 'fd-remove': {
        if (d === undefined) return;
        this.ctx.play('sfx-menu-toggle');
        removeRow(d, Number(el.dataset.i ?? -1));
        this.err = undefined;
        this.notice = '';
        this.ctx.refresh();
        return;
      }
      case 'fd-save':
        this.save();
        return;
      case 'fd-reset':
        if (!armedPress(this.ctx, this.gate, 'fd-reset')) return;
        this.run('reset', resetFleetNames, S.fleet_reset_done);
        return;
      default:
    }
  }

  /** SAVE: one press. The rune counts are checked here first; every
   *  other rule is the server's. */
  private save(): void {
    const d = this.draft;
    const stored = this.ctx.data.peek<string[]>(POOL_KEY).value;
    if (d === undefined || stored === undefined || this.busy !== '') return;
    const bad = precheck(d);
    if (bad !== null) {
      this.ctx.play('sfx-menu-error');
      this.err = {
        index: bad.index,
        text: bad.reason === 'short' ? S.too_short({ limit: bad.limit }) : S.too_long({ limit: bad.limit }),
      };
      this.notice = '';
      this.ctx.refresh();
      return;
    }
    if (!changed(d, stored)) {
      this.err = undefined;
      this.notice = S.saved;
      this.ctx.refresh();
      return;
    }
    const names = namesOf(d);
    this.run('save', () => putFleetNames(names), S.saved);
  }

  /** One call of the card. The answer is the stored pool, which becomes
   *  the draft; the cell refreshes behind it. */
  private run(kind: 'save' | 'reset', call: () => Promise<string[]>, done: string): void {
    this.busy = kind;
    this.err = undefined;
    this.notice = '';
    this.ctx.play('sfx-menu-press');
    this.ctx.refresh();
    call().then((names) => {
      this.busy = '';
      this.draft = draftOf(names);
      this.notice = done;
      this.ctx.data.expire(POOL_KEY);
      this.ctx.refresh();
    }, (err: unknown) => {
      this.busy = '';
      this.ctx.play('sfx-menu-error');
      this.err = { index: rowOf(err, this.draft), text: messageOf(err) };
      this.ctx.refresh();
    });
  }

  /**
   * The boxes. The text lives in the draft, and the markup carries it back
   * as a value, because the pane is rewritten whenever the markup changes.
   * A counter moves at once, without a rewrite.
   */
  wire(root: HTMLElement): void {
    root.querySelectorAll<HTMLInputElement>('[data-in^="fd-name-"]').forEach((box) => {
      if (!wireOnce(box)) return;
      const name = box.dataset.in ?? '';
      box.addEventListener('input', () => this.typed(root, name, box.value));
    });
  }

  /** One keystroke in one row. */
  private typed(root: HTMLElement, name: string, value: string): void {
    const d = this.draft;
    const i = Number(name.slice('fd-name-'.length));
    if (d === undefined || !(i >= 0 && i < d.rows.length)) return;
    d.rows[i] = value;
    this.notice = '';
    showCount(root, name, runeCount(tidySpaces(value)), FLEET_NAME_MAX_RUNES);
    const all = root.querySelector<HTMLElement>('[data-count="fd-all"]');
    if (all) all.textContent = S.fleet_count({ n: namesOf(d).length, max: FLEET_POOL_MAX_NAMES });
  }
}

/**
 * The row a server refusal names. The server names the refused entry as
 * the player wrote it, in the message; the row whose tidy text matches it
 * takes the line, and every other refusal stands under the card.
 */
function rowOf(err: unknown, d: PoolDraft | undefined): number {
  if (!(err instanceof ApiError) || err.code !== CODE_INVALID_FLEET_NAMES || d === undefined) return -1;
  const msg = err.message;
  for (let i = 0; i < d.rows.length; i++) {
    const name = tidySpaces(d.rows[i] ?? '');
    if (name !== '' && msg.includes(`"${name}"`)) return i;
  }
  return -1;
}
